import React, { useState,useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { signOut, updateProfile, fetchUser } from '../../redux/actions/authActions';
import { toast } from 'react-toastify';
import Orders from '../orders';
import Favourites from '../favourites';
import styles from '../../sass/profile.module.scss';

const Profile = () => {
  const { userDetails, token, loader, errors } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: userDetails?.name || '',
    mobile: userDetails?.mobile || '',
    email: userDetails?.email || ''
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('account');

  // Fetch fresh user data when component mounts
  useEffect(() => {
    if (token && userDetails?.id) {
      dispatch(fetchUser());
    }
  }, [dispatch, token, userDetails?.id]);

  // Update form data when userDetails changes
  useEffect(() => {
    if (userDetails) {
      setFormData({
        name: userDetails.name || '',
        mobile: userDetails.mobile || '',
        email: userDetails.email || ''
      });
    }
  }, [userDetails]);

  const handleLogout = async () => {
    try {
      await dispatch(signOut(token));
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if logout fails, redirect to login
      navigate('/login');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };


  const handleEditClick = () => {
    setFormData({
      name: userDetails?.name || '',
      mobile: userDetails?.mobile || '',
      email: userDetails?.email || ''
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (!loader) {
      setIsModalOpen(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(updateProfile(formData)).unwrap();
      toast.success('Profile updated successfully!');
      setIsModalOpen(false);
    } catch (error) {
      toast.error('Failed to update profile. Please try again.');
      console.error('Profile update failed:', error);
    }
  };


  return (
    <>
      <Helmet>
        <title>Profile - Dental Stores</title>
        <meta name="description" content="View your profile information including name and mobile number." />
      </Helmet>
      <div className={styles.profileContainer}>
        <h1 className={styles.profileTitle}>
          My Profile
        </h1>

        <div className={styles.tabs}>
          <button
            className={`${styles.tabButton} ${activeTab === 'account' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('account')}
          >
            My Account
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'orders' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            My Orders
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'wishlist' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('wishlist')}
          >
            My Wishlist
          </button>
        </div>

        {activeTab === 'account' && (
          <div className={styles.profileCard}>
            <div className={styles.profileHeader}>
              <div className={styles.profileInfo}>
                <div className={styles.profileAvatar}>
                  {userDetails?.name ? userDetails.name.charAt(0).toUpperCase() : 'U'}
                </div>
                <div>
                  <p className={styles.profileName}>
                    {userDetails?.name || 'Guest User'}
                  </p>
                  <p className={styles.profileSubtitle}>
                    Manage your personal information
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleEditClick}
                className={styles.editButton}
                aria-label="Edit profile"
              >
                <span aria-hidden="true">✏️</span>
                Edit
              </button>
            </div>

            <div className={styles.profileDetails}>
              <div className={styles.detailItem}>
                <p className={styles.detailLabel}>Full Name</p>
                <p className={styles.detailValue}>
                  {userDetails?.name || '—'}
                </p>
              </div>
              <div className={styles.detailItem}>
                <p className={styles.detailLabel}>Whatsapp Number</p>
                <p className={styles.detailValue}>
                  {userDetails?.mobile || '—'}
                </p>
              </div>
              <div className={styles.detailItem}>
                <p className={styles.detailLabel}>Email</p>
                <p className={styles.detailValue}>
                  {userDetails?.email || '—'}
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'account' && (
          <button
            type="button"
            onClick={handleLogout}
            className={styles.logoutButton}
          >
            Logout
          </button>
        )}

        {activeTab === 'orders' && (
          <div className={styles.tabContent}>
            <Orders />
          </div>
        )}

        {activeTab === 'wishlist' && (
          <div className={styles.tabContent}>
            <Favourites />
          </div>
        )}
      </div>

      {isModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="profile-edit-title"
          className={styles.modalOverlay}
          onClick={handleCloseModal}
        >
          <div
            className={styles.modalContent}
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2 id="profile-edit-title" className={styles.modalTitle}>
                Edit Profile
              </h2>
              <button
                type="button"
                onClick={handleCloseModal}
                className={styles.closeButton}
                aria-label="Close edit profile dialog"
                disabled={loader}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={styles.formInput}
                  placeholder="Enter your full name"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Whatsapp Number
                </label>
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  className={styles.formInput}
                  placeholder="Enter your Whatsapp number"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Email (read only)
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  readOnly
                  className={styles.formInput}
                  placeholder="Your email ID"
                />
              </div>

              {errors && (
                <div className={styles.errorMessage}>
                  {typeof errors === 'string' ? errors : JSON.stringify(errors)}
                </div>
              )}

              <div className={styles.buttonGroup}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={loader}
                  className={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loader}
                  className={styles.saveButton}
                >
                  {loader ? 'Updating...' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Profile;
